import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Bell, Snowflake, Wallet, Search, Send, AlertTriangle } from 'lucide-react';

export const SecurityPanel = ({ headers }) => {
  const [overview, setOverview] = useState(null);
  const [recipientQ, setRecipientQ] = useState('');
  const [recipientResults, setRecipientResults] = useState(null);
  const [sweepMode, setSweepMode] = useState('new');
  const [sweepDest, setSweepDest] = useState('');
  const [sweepConfirm, setSweepConfirm] = useState('');
  const [sweepLoading, setSweepLoading] = useState(false);
  const [sweepResult, setSweepResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/security`, { headers });
      setOverview(r.data);
    } catch { toast.error('Failed to load security settings'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const searchRecipient = async () => {
    if (!recipientQ.trim()) return;
    try {
      const r = await axios.get(`${API}/admin/users?q=${encodeURIComponent(recipientQ)}&limit=10`, { headers });
      setRecipientResults(r.data.users || []);
    } catch { toast.error('Search failed'); }
  };

  const setRecipient = async (userId) => {
    try {
      await axios.post(`${API}/admin/security/settings`, { alert_user_id: userId }, { headers });
      toast.success(userId ? 'Alert recipient saved' : 'Alert recipient cleared');
      setRecipientResults(null); setRecipientQ('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save recipient'); }
  };

  const toggleFreeze = async (frozen) => {
    try {
      await axios.post(`${API}/admin/security/settings`, { withdrawals_frozen: frozen }, { headers });
      toast.success(frozen ? 'Withdrawals FROZEN — all payouts blocked' : 'Withdrawals resumed');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const sendTest = async () => {
    try {
      await axios.post(`${API}/admin/security/test-alert`, {}, { headers });
      toast.success('Test DM sent — check Telegram');
    } catch (e) { toast.error(e.response?.data?.detail || 'Test alert failed'); }
  };

  const runSweep = async () => {
    setSweepLoading(true); setSweepResult(null);
    try {
      const r = await axios.post(`${API}/admin/security/sweep`, {
        mode: sweepMode,
        destination: sweepMode === 'external' ? sweepDest.trim() : undefined,
        confirm: sweepConfirm.trim(),
      }, { headers });
      setSweepResult(r.data);
      if (r.data.success) { toast.success('Sweep complete'); setSweepConfirm(''); load(); }
      else toast.error(r.data.error || 'Sweep failed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sweep failed');
    } finally { setSweepLoading(false); }
  };

  const s = overview?.settings || {};

  return (
    <div className="space-y-4" data-testid="security-panel">
      {/* Alert recipient */}
      <Card className="bg-slate-800/60 border-slate-700/50 p-4 space-y-3">
        <div className="flex items-center gap-2 text-indigo-300 font-semibold text-sm"><Bell className="w-4 h-4" /> Security Alert DMs</div>
        <p className="text-xs text-slate-400">The bot DMs this Telegram account on every withdrawal, admin login (success & failed), freeze change and wallet sweep.</p>
        {s.alert_telegram_id ? (
          <div className="flex items-center justify-between bg-slate-900/60 rounded-lg px-3 py-2">
            <span className="text-sm text-emerald-300" data-testid="security-alert-recipient">@{s.alert_username || s.alert_telegram_id}</span>
            <div className="flex gap-2">
              <Button data-testid="security-test-alert-btn" size="sm" variant="outline" className="h-7 text-xs border-slate-600" onClick={sendTest}><Send className="w-3 h-3 mr-1" />Test DM</Button>
              <Button data-testid="security-clear-recipient-btn" size="sm" variant="ghost" className="h-7 text-xs text-red-400" onClick={() => setRecipient(null)}>Clear</Button>
            </div>
          </div>
        ) : (
          <p className="text-xs text-amber-400" data-testid="security-no-recipient">No recipient set — alerts are logged but not DMed yet.</p>
        )}
        <div className="flex gap-2">
          <Input data-testid="security-recipient-search-input" value={recipientQ} onChange={(e) => setRecipientQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && searchRecipient()} placeholder="Search your username…" className="h-8 text-xs bg-slate-900/60 border-slate-700" />
          <Button data-testid="security-recipient-search-btn" size="sm" className="h-8" onClick={searchRecipient}><Search className="w-3.5 h-3.5" /></Button>
        </div>
        {recipientResults && (
          <div className="space-y-1">
            {recipientResults.length === 0 && <p className="text-xs text-slate-500">No matches</p>}
            {recipientResults.map((u) => (
              <button key={u.user_id} data-testid={`security-pick-${u.username}`} onClick={() => setRecipient(u.user_id)} className="w-full text-left bg-slate-900/60 hover:bg-slate-700/60 rounded-lg px-3 py-2 text-xs text-slate-200">
                @{u.username} {u.telegram_username ? <span className="text-slate-500">(tg: {u.telegram_username})</span> : <span className="text-amber-500">(no Telegram linked)</span>}
              </button>
            ))}
          </div>
        )}
      </Card>

      {/* Freeze switch */}
      <Card className={`p-4 border ${s.withdrawals_frozen ? 'bg-red-950/40 border-red-700/60' : 'bg-slate-800/60 border-slate-700/50'}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 font-semibold text-sm text-red-300"><Snowflake className="w-4 h-4" /> Emergency Withdrawal Freeze</div>
            <p className="text-xs text-slate-400 mt-1">Instantly blocks ALL user withdrawals. Use if you suspect unauthorized access. On-chain transfers already sent cannot be reversed.</p>
          </div>
          <Switch data-testid="security-freeze-switch" checked={!!s.withdrawals_frozen} onCheckedChange={toggleFreeze} />
        </div>
        {s.withdrawals_frozen && <p className="text-xs text-red-400 mt-2 font-semibold" data-testid="security-frozen-banner">❄️ Withdrawals are currently FROZEN</p>}
      </Card>

      {/* Sweep tool */}
      <Card className="bg-slate-800/60 border-orange-700/40 p-4 space-y-3">
        <div className="flex items-center gap-2 text-orange-300 font-semibold text-sm"><Wallet className="w-4 h-4" /> One-Click Wallet Sweep</div>
        <div className="bg-slate-900/60 rounded-lg px-3 py-2 text-xs text-slate-300 space-y-0.5">
          <div>Platform wallet: <span className="font-mono text-slate-400" data-testid="security-platform-address">{overview?.platform_address || '…'}</span></div>
          <div data-testid="security-balances">Balance: <span className="text-emerald-300">{overview?.xrp_balance ?? '—'} XRP</span> · <span className="text-yellow-300">{overview?.fuzzy_balance ?? 0} FUZZY</span></div>
        </div>
        <div className="flex gap-2">
          <button data-testid="sweep-mode-new" onClick={() => setSweepMode('new')} className={`flex-1 rounded-lg px-3 py-2 text-xs border ${sweepMode === 'new' ? 'bg-orange-500/20 border-orange-500/60 text-orange-200' : 'bg-slate-900/60 border-slate-700 text-slate-400'}`}>
            Brand-new app wallet (auto)
          </button>
          <button data-testid="sweep-mode-external" onClick={() => setSweepMode('external')} className={`flex-1 rounded-lg px-3 py-2 text-xs border ${sweepMode === 'external' ? 'bg-orange-500/20 border-orange-500/60 text-orange-200' : 'bg-slate-900/60 border-slate-700 text-slate-400'}`}>
            My Xaman address
          </button>
        </div>
        {sweepMode === 'external' && (
          <div className="space-y-1">
            <Input data-testid="sweep-destination-input" value={sweepDest} onChange={(e) => setSweepDest(e.target.value)} placeholder="r… (your Xaman address, needs FUZZY trustline)" className="h-8 text-xs font-mono bg-slate-900/60 border-slate-700" />
            <p className="text-[10px] text-amber-400">⚠️ Funds leave the app — withdrawals will stall until you fund the app wallet again.</p>
          </div>
        )}
        {sweepMode === 'new' && (
          <p className="text-[10px] text-slate-400">Creates a fresh wallet, activates it, sets the FUZZY trustline, moves everything and switches the app over. Deposit address updates automatically. ~1 XRP base reserve stays locked on the old wallet.</p>
        )}
        <Input data-testid="sweep-confirm-input" value={sweepConfirm} onChange={(e) => setSweepConfirm(e.target.value)} placeholder='Type SWEEP to confirm' className="h-8 text-xs bg-slate-900/60 border-slate-700" />
        <Button data-testid="sweep-execute-btn" disabled={sweepLoading || sweepConfirm.trim() !== 'SWEEP' || (sweepMode === 'external' && !sweepDest.trim())} onClick={runSweep} className="w-full bg-orange-600 hover:bg-orange-500 text-white h-9 text-sm">
          <AlertTriangle className="w-4 h-4 mr-1" /> {sweepLoading ? 'Sweeping… (can take ~30s)' : 'Sweep All Funds Now'}
        </Button>
        {sweepResult && (
          <div data-testid="sweep-result" className={`rounded-lg px-3 py-2 text-xs space-y-1 ${sweepResult.success ? 'bg-emerald-950/40 text-emerald-300' : 'bg-red-950/40 text-red-300'}`}>
            <div className="font-semibold">{sweepResult.success ? '✅ Sweep complete' : `❌ ${sweepResult.error}`}</div>
            {sweepResult.new_address && <div>New wallet: <span className="font-mono">{sweepResult.new_address}</span></div>}
            <div>Moved: {sweepResult.xrp_moved ?? 0} XRP · {sweepResult.fuzzy_moved ?? 0} FUZZY</div>
            {(sweepResult.steps || []).map((st, i) => (
              <div key={i} className="text-[10px] text-slate-400">{st.ok ? '✓' : '✗'} {st.step}{st.tx_hash ? ` — ${st.tx_hash.slice(0, 12)}…` : ''}{st.error ? ` (${st.error})` : ''}</div>
            ))}
            {sweepResult.note && <div className="text-[10px] text-amber-400">{sweepResult.note}</div>}
          </div>
        )}
      </Card>

      {/* Recent events */}
      <Card className="bg-slate-800/60 border-slate-700/50 p-4 space-y-2">
        <div className="text-sm font-semibold text-slate-300">Recent Security Events</div>
        <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="security-events-list">
          {(overview?.events || []).length === 0 && <p className="text-xs text-slate-500">No events yet</p>}
          {(overview?.events || []).map((ev, i) => (
            <div key={i} className="bg-slate-900/60 rounded-lg px-3 py-1.5 text-[11px] text-slate-300 whitespace-pre-wrap">
              <span className="text-indigo-400 font-mono">[{ev.alert_type}]</span> {ev.text}
              <div className="text-[9px] text-slate-500">{new Date(ev.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default SecurityPanel;
